import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  NavLink,
  DataTable,
  Button,
  WorkflowNameInput,
  WorkflowNameSingleInput,
  PrimaryButton,
  Dropdown,
} from "../../components";
import { makeStyles } from "@material-ui/styles";
import _, { set } from "lodash";
import { useQueryState } from "react-router-use-location-state";
import {
  useLatestWorkflowDefs,
  useWorkflowVersionsWithEnabled,
} from "../../data/workflow";
import Header from "./Header";
import sharedStyles, { dataTableStyles } from "../styles";
import { Helmet } from "react-helmet";
import {
  Paper,
  IconButton,
  Snackbar,
  Grid,
  FormControl,
  Input,
} from "@material-ui/core";
import { Tooltip } from "@material-ui/core";
import { timestampRenderer } from "../../utils/helpers";
import clsx from "clsx";
import PlayArrowOutlinedIcon from "@material-ui/icons/PlayArrowOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  deleteEntireWorkflowApi,
  deleteWorkflowApi,
  getCategories,
} from "../../data/services/API";
import ConfirmChoiceDialog from "../../components/ConfirmChoiceDialog";
import { useHistory, useLocation } from "react-router-dom";
import { colors } from "../../theme/variables";
import CloseIcon from "@material-ui/icons/Close";
import DateRangePicker from "../../components/DateRangePicker";
import FormikDropdown from "../../components/formik/FormikDropdown";
import executionsStyles from "./../executions/executionsStyles";

const useStyles = makeStyles({
  ...sharedStyles,
  ...executionsStyles,
  inputWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    marginBottom: 1,
    marginLeft: 2,
    fontSize: 10,
    fontWeight: 600,
    color: "#000",
  },
  subheader: {
    minHeight: "150px",
    alignItems: "center",
    justifyContent: "space-evenly",
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
    backgroundColor: "#f4f6ff",
  },
});
const caseInsensitiveSort = (rowA, rowB) => {
  const nameA = (rowA.workflowName || rowA.name || "").toLowerCase();
  const nameB = (rowB.workflowName || rowB.name || "").toLowerCase();

  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

export default function WorkflowDefinitions(props) {
  const permissions = props.permissions;

  const columns = [
    {
      name: "workflowName",
      sortFunction: caseInsensitiveSort,
      minWidth: "300px",
      label: (
        <Tooltip title="Name">
          <div>Name</div>
        </Tooltip>
      ),
      renderer: (val, row) => (
        <Tooltip title={val}>
          <NavLink path={`/workflowDef/${row?.name?.trim()}`}>
            {row?.workflowName?.trim() || row?.name?.trim()}
          </NavLink>
        </Tooltip>
      ),
      grow: 0.5,
      wrap: false,
    },
    {
      name: "category",
      maxWidth: "150px",
      label: (
        <Tooltip title="Type">
          <div>Type</div>
        </Tooltip>
      ),
      renderer: (val) => (
        <Tooltip title={val}>
          <div>{val}</div>
        </Tooltip>
      ),
      grow: 0.5,
      wrap: false,
    },
    {
      name: "description",
      label: (
        <Tooltip title="Description">
          <div>Description</div>
        </Tooltip>
      ),
      grow: 2,
      width: "400px",
      wrap: false,
      renderer: (val) => (
        <Tooltip title={val}>
          <div>{val}</div>
        </Tooltip>
      ),
    },
    {
      name: "createTime",
      type: "date",
      width: "200px",
      label: (
        <Tooltip title="Create Time">
          <div>Create Time</div>
        </Tooltip>
      ),
      renderer: (val) => (
        <Tooltip title={timestampRenderer(val)}>
          <div>{timestampRenderer(val)}</div>
        </Tooltip>
      ),
    },
    {
      name: "version",
      width: "200px",
      label: (
        <Tooltip title="Latest Version">
          <div>Latest Version</div>
        </Tooltip>
      ),
      grow: 1,
      center: true,
    },
    {
      name: "schemaVersion",
      label: (
        <Tooltip title="Schema Version">
          <div>Schema Version</div>
        </Tooltip>
      ),
      grow: 1,
      center: true,
    },
    {
      name: "restartable",
      width: "150px",
      label: (
        <Tooltip title="Restartable">
          <div>Restartable</div>
        </Tooltip>
      ),
      grow: 0.5,
    },
    {
      name: "workflowStatusListenerEnabled",
      label: (
        <Tooltip title="Workflow Status Listener Enabled">
          <div>Workflow Status Listener Enabled</div>
        </Tooltip>
      ),
      // grow: 0.5,
    },
    {
      name: "ownerEmail",
      width: "250px",
      label: (
        <Tooltip title="Owner Email">
          <div>Owner Email</div>
        </Tooltip>
      ),
      wrap: false,
      renderer: (val) => (
        <Tooltip title={val}>
          <div>{val}</div>
        </Tooltip>
      ),
    },
    {
      name: "inputParameters",
      label: (
        <Tooltip title="Input Parameters">
          <div>Input Parameters</div>
        </Tooltip>
      ),
      type: "json",

      renderer: (val) =>
        _.isArray(val) ? (
          <Tooltip title={val}>
            <div>{val}</div>
          </Tooltip>
        ) : (
          []
        ),
    },
    {
      name: "outputParameters",
      label: (
        <Tooltip title="Output Parameters">
          <div>Output Parameters</div>
        </Tooltip>
      ),
      type: "json",

      grow: 2,
      wrap: false,
      renderer: (val) => {
        return (
          <Tooltip title={JSON.stringify(val)}>
            <div>{JSON.stringify(val)}</div>
          </Tooltip>
        );
      },
    },
    {
      name: "timeoutPolicy",
      label: (
        <Tooltip title="Timeout Policy">
          <div>Timeout Policy</div>
        </Tooltip>
      ),
      // grow: 0.5,
    },
    {
      name: "timeoutSeconds",
      label: (
        <Tooltip title="Timeout Seconds">
          <div>Timeout Seconds</div>
        </Tooltip>
      ),
      // grow: 0.5,
    },
    {
      id: "task_types",
      name: "tasks",
      label: (
        <Tooltip title="Task Types">
          <div>Task Types</div>
        </Tooltip>
      ),
      searchable: "calculated",

      wrap: false,
      renderer: (val) => {
        const taskTypeSet = new Set();
        for (let task of val) {
          taskTypeSet.add(task.type);
        }
        return (
          <Tooltip title={Array.from(taskTypeSet).join(", ")}>
            <div>{Array.from(taskTypeSet).join(", ")}</div>
          </Tooltip>
        );
      },
    },
    {
      id: "task_count",
      name: "tasks",
      label: (
        <Tooltip title="Tasks">
          <div>Tasks</div>
        </Tooltip>
      ),
      searchable: "calculated",

      center: true,
      grow: 0.5,
      renderer: (val) =>
        _.isArray(val) ? (
          <Tooltip title={val.length}>
            <div>{val.length}</div>
          </Tooltip>
        ) : (
          0
        ),
    },
    {
      id: "executions_link",
      name: "name",
      label: (
        <Tooltip title="Executions">
          <div>Executions</div>
        </Tooltip>
      ),

      searchable: false,
      sortable: false,
      grow: 0.5,
      renderer: (name) => (
        <NavLink path={`/search?workflowType=${name.trim()}`}>Query</NavLink>
      ),
    },
    {
      id: "actions",
      name: "Actions",
      label: (
        <Tooltip title="Actions">
          <div>Actions</div>
        </Tooltip>
      ),

      searchable: false,
      sortable: false,
      grow: 0.5,
      type: "action",
      renderer: (val, row) => <Actions val={val} row={row}></Actions>,
    },
  ];

  const [clickedDeleteBtn, setClickedDeleteBtn] = useState({
    row: null,
    openDialog: false,
    header: "",
    message: "",
  });

  const classes = useStyles();

  const [filterParam, setFilterParam] = useQueryState("filter", "");
  const [page, setPage] = useQueryState("page", 1);
  const [rowsPerPage, setRowsPerPage] = useQueryState("rowsPerPage", 15);
  const [showSubHeader, setShowSubHeader] = useState(false);
  const [selectedWorkflowName, setSelectedWorkflowName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);

  const setWFName = (val) => {
    setSelectedWorkflowName(val);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getCategories();
        setCategories(response.data);
      } catch (error) {}
    };

    fetchData();
  }, []);

  const handleExecutionFilterClick = (e) => {
    e.stopPropagation();
    setShowSubHeader(!showSubHeader);
  };

  const resetSearch = () => {
    setShowSubHeader(false);
  };

  const filterObj = filterParam === "" ? undefined : JSON.parse(filterParam);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [apiParams, setApiParams] = useState(null);
  const [workflowToBeDeleted, setworkflowTobedeleted] = useState("");
  const [workflowVersions, setWorkflowversions] = useState([]);
  // Call the hook unconditionally
  const { data, totalCount, isFetching, refetch } = useLatestWorkflowDefs(
    page,
    rowsPerPage,
    // apiParams?.selectedWorkflowName,
    // apiParams?.selectedCategory,
    selectedWorkflowName,
    selectedCategory,
    {
      enabled: !!apiParams, // Ensure the hook is not called until apiParams is set
    }
  );
  useEffect(() => {
    if (searchTriggered && apiParams) {
      refetch(); // Call the refetch method to trigger the API call
      setSearchTriggered(false); // Reset the search trigger
    }
  }, [searchTriggered, apiParams]);

  const { data: wfVersions, refetch: refetchwfVersions ,  isFetching: isFetchingwfVersions,isLoading:isLoadingwfVersions } =
    useWorkflowVersionsWithEnabled(workflowToBeDeleted, {
      enabled: !!workflowToBeDeleted,
    });

  useEffect(() => {
    if (workflowToBeDeleted) {
     
      refetchwfVersions();
    }
  }, [workflowToBeDeleted]);

  useEffect(() => {
   
   if(wfVersions !== null && wfVersions !== undefined && workflowToBeDeleted )
   {
  
    setWorkflowversions(wfVersions);
   }
   
  }, [wfVersions,workflowToBeDeleted]);

  const doSearch = () => {
    setApiParams({
      page: 1,
      rowsPerPage,
      selectedWorkflowName,
      selectedCategory,
    });
    setSearchTriggered(true);
  };
  const [totalHits, setTotalHits] = useState(0);
  const location = useLocation();

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const error = queryParams.get("error");

    if (error === "true") {
      setMessageSnackbar({
        message: "Workflow Not found!",
        severity: "error",
        open: true,
        autoHideDuration: 3000,
        onClose: () => {
          setMessageSnackbar(null);
        },
      });
    }
  }, [location]);

  useEffect(() => {
    if (totalCount > 0) {
      setTotalHits(totalCount);
    }
  }, [totalCount]);

  // let totalHits = 0;

  // if (data) {
  //   if (totalCount) {
  //     totalHits = totalCount;
  //   } else {
  //     if (data.workflowDefinitions) {
  //       totalHits = data.workflowDefinitions.length;
  //     }
  //   }
  // }

  const handleChangeRowsPerPage = (limit) => {
    setRowsPerPage(limit);
  };

  useEffect(() => {
    refetch();
  }, [rowsPerPage]);

  const handleFilterChange = (obj) => {
    if (obj) {
      setFilterParam(JSON.stringify(obj));
    } else {
      setFilterParam("");
    }
  };

  const workflows = useMemo(() => {
    // Extract latest versions only
    if (data) {
      const unique = new Map();
      const types = new Set();
      for (let workflowDef of data) {
        if (!unique.has(workflowDef.name)) {
          unique.set(workflowDef.name, workflowDef);
        } else if (unique.get(workflowDef.name).version < workflowDef.version) {
          unique.set(workflowDef.name, workflowDef);
        }
        if (workflowDef?.tasks) {
          for (let task of workflowDef.tasks) {
            types.add(task.type);
          }
        }
      }
      var uniqueValues = Array.from(unique.values());
      var sortedUniqueValues = uniqueValues.sort(
        (b, a) => a.createTime - b.createTime
      );
      return sortedUniqueValues;
    }
  }, [data]);

  const Actions = (props) => {
    return (
      <div className="flex-row workflow-action-btns">
        {permissions?.workflows?.actions?.run === "True" ? (
          <Tooltip title="Run">
            <IconButton
              onClick={() => {
                runWorkflow(props.row);
              }}
              color={"default"}
              className="run-icon primary"
            >
              <PlayArrowOutlinedIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        {permissions?.workflows?.actions?.delete === "True" ? (
          <Tooltip title="Delete">
            <IconButton
              onClick={() => {
                handleWorkflowDeleteBtnClick(props.row);
              }}
              color={"default"}
              className="warn"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>
    );
  };

  const history = useHistory();
  function runWorkflow(row) {
    history.push("/workbench/" + row.name + "/" + row.version);
  }

  const [messageSnackbar, setMessageSnackbar] = useState(null);

  async function deleteWorkflow(
    row,
    deleteEntireWorkflow = false,
    versionNumber
  ) {
    if (row) {
      try {
        const response = deleteEntireWorkflow
          ? await deleteEntireWorkflowApi(row.name)
          : await deleteWorkflowApi(row.name, versionNumber);
        if (response?.status === 200) {
          setMessageSnackbar({
            message: response?.data
              ? response.data
              : "Workflow deleted successfully",
            severity: "success",
            open: true,
            autoHideDuration: 3000,
            onClose: () => {
              setMessageSnackbar(null);
            },
          });
          setClickedDeleteBtn({
            row: null,
            openDialog: false,
            header: "",
            message: "",
          });
          refetch();
          refetchwfVersions();
          setworkflowTobedeleted("");
          setWorkflowversions([]);
        } else {
          setMessageSnackbar({
            message: "Something went wrong.",
            severity: "error",
            open: true,
            autoHideDuration: 3000,
            onClose: () => {
              setMessageSnackbar(null);
            },
          });
          setClickedDeleteBtn({
            row: null,
            openDialog: false,
            header: "",
            message: "",
          });
        }
      } catch (err) {
        setMessageSnackbar({
          message: "Something went wrong.",
          severity: "error",
          open: true,
          autoHideDuration: 3000,
          onClose: () => {
            setMessageSnackbar(null);
          },
        });
        setClickedDeleteBtn({
          row: null,
          openDialog: false,
          header: "",
          message: "",
        });
      }
    }
  }

  const handleWorkflowDeleteBtnClick = (row) => {
    setworkflowTobedeleted(row.name);
    setClickedDeleteBtn({
      row: row,
      openDialog: true,
      header: "Delete Workflow",
      message: `This action will delete version the selected version of workflow ${row.name}.`,
    });
  };

  const ConformationDialog = useMemo(() => {
    const { openDialog, header, message, row } = clickedDeleteBtn;
    if (openDialog) {
      return (
        <ConfirmChoiceDialog
          header={header}
          message={message}
          versions={workflowVersions?workflowVersions:[]}
          loading = {isFetchingwfVersions}
          handleConfirmationValue={(confirm, deleteAll, versionNumber) => {
            if (confirm) {
              deleteWorkflow(row, deleteAll, versionNumber);
            } else {
              setworkflowTobedeleted("");
              setClickedDeleteBtn({ 
                row: null,
                openDialog: false,
                header: "",
                message: "",
              });
              setWorkflowversions([]);
            }
          }}
          open={openDialog}
          confirmText="Delete"
          dialogType="DeleteWorkflow"
          additionalCheckBox={"Delete All Versions"}
          additionalCheckBoxAction={(checked) => {
            // deleteWorkflow(row, true);
            if (checked) {
              setClickedDeleteBtn({
                ...clickedDeleteBtn,
                message:
                  "WARNING: This action will delete all the versions of " +
                  row.name,
              });
            } else {
              setClickedDeleteBtn({
                ...clickedDeleteBtn,
                message:
                  "This action will delete the selected version " +
                  " of workflow " +
                  row.name +
                  ".",
              });
            }
          }}
        />
      );
    } else {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedDeleteBtn, workflowVersions,isFetchingwfVersions]);

  return (
    <>
      <div className={[classes.wrapper]}>
        <Paper className={clsx([classes.paper], [classes.contentContainer])}>
          <Helmet>
            <title>Conductor UI - Workflow Definitions</title>
          </Helmet>
          <Header tabIndex={0} loading={isFetching} permissions={permissions} />
          {workflows && (
            <DataTable
              title={`${workflows.length} results`}
              localStorageKey="definitionsTable"
              defaultShowColumns={[
                // "name",
                "workflowName",
                "description",
                "version",
                "createTime",
                "ownerEmail",
                "task_count",
                "executions_link",
                "actions",
              ]}
              keyField="name"
              onFilterChange={handleFilterChange}
              initialFilterObj={filterObj}
              data={workflows}
              columns={
                !permissions?.workflows?.actions ||
                (permissions?.workflows?.actions.run === "False" &&
                  permissions?.workflows?.actions.delete === "False")
                  ? columns.filter(({ name }) => name !== "Actions")
                  : columns
              }
              customStyles={dataTableStyles}
              fixedHeader
              fixedHeaderScrollHeight="calc( 100vh - 270px )"
              showRefresh={true}
              paginationServer={true}
              executionFilter
              handleExecutionFilterClick={handleExecutionFilterClick}
              subHeader={showSubHeader}
              subHeaderComponent={
                <div className={classes.subheader}>
                  <Grid container spacing={2} className={`${classes.controls}`}>
                    <Grid item xs={4}>
                      <div className={classes.inputWrapper}>
                        <WorkflowNameSingleInput
                          fullWidth
                          placeholder="Workflow Name"
                          onChange={(evt, val) => setWFName(val)}
                          value={selectedWorkflowName}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={4}>
                      <Dropdown
                        id="category"
                        label="Type"
                        name="category"
                        variant="outlined"
                        fullWidth
                        freeSolo
                        options={categories.map((obj) => obj)}
                        onChange={(evt, val) => setSelectedCategory(val)}
                        value={selectedCategory}
                        // options={categories.map((obj) => obj.categoryName)}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={0}
                      style={{
                        alignItems: "center",
                      }}
                    >
                      <FormControl>
                        <PrimaryButton
                          className={classes.btnSearch}
                          onClick={doSearch}
                        >
                          Search
                        </PrimaryButton>
                      </FormControl>
                    </Grid>
                    <Grid
                      item
                      xs={0}
                      style={{
                        alignItems: "center",
                      }}
                    >
                      <FormControl>
                        <Button color="outlinedPrimary" onClick={resetSearch}>
                          Cancel
                        </Button>
                      </FormControl>
                    </Grid>
                  </Grid>
                </div>
              }
              paginationTotalRows={totalHits}
              paginationDefaultPage={page}
              paginationPerPage={rowsPerPage}
              onChangeRowsPerPage={(rowsPerPage) =>
                handleChangeRowsPerPage(rowsPerPage)
              }
              onChangePage={(page) => {
                setPage(page);
                refetch();
              }}
              handleRefreshClick={() => {
                refetch();
              }}
              customBtn={
                permissions?.workflows?.actions?.create_workflow === "True"
                  ? {
                      className: classes.buttonRowWorkFlow,
                      link: "/workflowDef",
                      btnText: "New Workflow Definition",
                    }
                  : null
              }
            />
          )}
        </Paper>
      </div>
      {ConformationDialog}
      {messageSnackbar && (
        <Snackbar
          {...messageSnackbar}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          action={
            <React.Fragment>
              <IconButton
                aria-label="close"
                color="inherit"
                className={classes.close}
                onClick={() => setMessageSnackbar(null)}
              >
                <CloseIcon />
              </IconButton>
            </React.Fragment>
          }
        />
      )}
    </>
  );
}
